//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  InstagramReelsPostCustomizations,
  InstagramVideoMetadata,
  ScheduledPostTimeUpdate,
} from '@bvengineering/universal-scheduling-gateway-service-types';
import moment, { Moment } from 'moment-timezone';
import { PojoMap } from 'pojo-maps';

import {
  InstagramLocation,
  UniversalScheduledPostConfigType,
} from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import {
  IDraftInstagramPost,
  IDraftPost,
} from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import {
  IScheduledInstagramPost,
  IScheduledPost,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import {
  isDraftPost,
  isScheduledPost,
} from '@apis/universal-posts/universalPostsUtils';
import {
  FeedPreviewMediaOverlayIconType,
  IFeedPreviewItem,
} from '@instagram-account/redux/schedule/feed-preview/instagramScheduleFeedPreviewModel';
import {
  IWeekViewItemTime,
  InstagramScheduleDateChangeOffset,
  NormalizedScheduledPosts,
} from '@instagram-account/redux/schedule/instagramScheduleModel';
import { getCurrentStartDate } from '@instagram-account/redux/schedule/selectors/getCurrentStartDate';
import { ICuralateStore } from '@models/AppState';
import Cuid from '@models/Cuid';
import DateString from '@models/DateString';
import { IInstagramCarouselItem } from '@models/IInstagramCarouselItem';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { INetworkItem, WithVideo, isVideo } from '@models/INetworkItem';
import {
  IInstagramStoryGroupClip,
  InstagramStoryGroupClipCustomizationType,
} from '@models/IScheduledInstagramStoryGroup';
import { IShopTag, toShopTag } from '@models/IShopTag';
import { ITaggedUser, fromInstagramTaggedUser } from '@models/ITaggedUser';
import { InstagramPostStatus } from '@models/InstagramPostStatus';
import { InstagramPostType } from '@models/InstagramPostType';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import { SocialNetwork } from '@models/SocialNetwork';
import TimeZoneId from '@models/TimeZoneId';
import { UniversalPostableCustomStatus } from '@models/UniversalPostableCustomStatus';
import UserId from '@models/UserId';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import {
  ScheduleOptions,
  UniversalCalendarView,
} from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { isDefined } from '@utils/filterPredicates';
import { mergeObjects } from '@utils/mergeObjects';
import { Normalized } from '@utils/normalize';
import { sortBy } from '@utils/sortBy';

/**
 * Applies a day offset to a start date and returns the resulting timezoned
 * moment for use in formatting calls and other helpers.
 */
export const getTimezonedMomentFromStartDateOffset = (
  startDate: DateString,
  offset: number,
  tz: TimeZoneId,
) => DateString.toMoment(startDate, tz).add(offset, 'days');

/**
 * Applies a day and minuteOfDay offset to the startDate, per the given timezone.
 *
 * Ex:
 *  - StartDate = '2020-04-01', TZ = PST
 *  - Offset = { dayOffset: 1, minuteOfDay: 600 }
 *  - Output = Moment representing 4/2/2020, 10:00 AM PST
 */
export const applyDndOffsetToStartDate = (
  startDate: DateString,
  { dayOffset, minuteOfDay }: IWeekViewItemTime,
  tz: TimeZoneId,
) => {
  const m = getTimezonedMomentFromStartDateOffset(startDate, dayOffset, tz);
  return m.add(minuteOfDay, 'minutes');
};

/**
 * Calculates the startDate for the week view given a particular date by determining
 * the startOfWeek day prior to the given date.
 */
export const getWeekViewStartDate = (
  date: DateString,
  tz: TimeZoneId,
  startOfWeek: number,
) => {
  const dateMoment = DateString.toMoment(date, tz);
  const dateDay = dateMoment.day();

  if (dateDay === startOfWeek) {
    return date;
  } else if (dateDay > startOfWeek) {
    return DateString.applyMomentSetter(date, 'day', startOfWeek);
  } else {
    const previousWeek = DateString.applyMomentOperation(
      date,
      'subtract',
      1,
      'w',
    );
    return DateString.applyMomentSetter(previousWeek, 'day', startOfWeek);
  }
};

/**
 * Calculates the startDate for the month view given a particular date by determining
 * the startOfWeekDay prior to the start of the month of the given date.
 */
export const getMonthViewStartDate = (
  date: DateString,
  tz: TimeZoneId,
  startOfWeek: number,
) => {
  const startOfMonthDateString = DateString.applyMomentSetter(date, 'date', 1);
  return getWeekViewStartDate(startOfMonthDateString, tz, startOfWeek);
};

/**
 * There is no guarantee that the startDate at a given time is in the month that the calendar
 * is currently representing, but there is a guarantee that the end of the week is. Returns the
 * first day of the current month shown in the calendar.
 */
export const getStartOfCurrentMonth = (startDate: DateString) => {
  const endOfStartDateWeek = DateString.addDays(startDate, 6);
  return DateString.applyMomentSetter(endOfStartDateWeek, 'date', 1);
};

/**
 * Calculates the startDate for the given view when we navigate forwards in time.
 * For timeline and week views we can navigate with just an offset, but month view
 * requires navigating to the start of the next month.
 */
export const getNextStartDate = (
  view: UniversalCalendarView,
  startDate: DateString,
  tz: TimeZoneId,
  startOfWeek: number,
) => {
  switch (view) {
    case UniversalCalendarView.Week:
    case UniversalCalendarView.Timeline: {
      const offset = InstagramScheduleDateChangeOffset[view];
      return DateString.addDays(startDate, offset);
    }
    case UniversalCalendarView.Month: {
      const startOfNextMonth = DateString.applyMomentOperation(
        getStartOfCurrentMonth(startDate),
        'add',
        1,
        'M',
      );
      return getMonthViewStartDate(startOfNextMonth, tz, startOfWeek);
    }
  }
};

/**
 * Calculates the startDate for the given view when we navigate backwards in time.
 * For timeline and week views we can navigate with just an offset, but month view
 * requires navigating to the start of the previous month.
 */
export const getPreviousStartDate = (
  view: UniversalCalendarView,
  startDate: DateString,
  tz: TimeZoneId,
  startOfWeek: number,
) => {
  switch (view) {
    case UniversalCalendarView.Week:
    case UniversalCalendarView.Timeline: {
      const offset = InstagramScheduleDateChangeOffset[view];
      return DateString.addDays(startDate, offset * -1);
    }
    case UniversalCalendarView.Month: {
      const startOfPreviousMonth = DateString.applyMomentOperation(
        getStartOfCurrentMonth(startDate),
        'subtract',
        1,
        'M',
      );
      return getMonthViewStartDate(startOfPreviousMonth, tz, startOfWeek);
    }
  }
};

/**
 * Creates a temporary copy of the current scheduled posts with the
 * post being rescheduled swapped out for a version with the new post time.
 * Resorts the list by scheduledTime so that it feeds through the data
 * selectors properly.
 */
export const getTemporaryScheduledPosts = (
  scheduledPosts: NormalizedScheduledPosts,
  postId: PostableId,
  newScheduledTime: Date,
): NormalizedScheduledPosts => {
  const rescheduledPost = Normalized.getById(scheduledPosts, postId);

  if (!rescheduledPost) {
    return scheduledPosts;
  }

  const byId = PojoMap.set(scheduledPosts.byId, postId, {
    ...rescheduledPost,
    scheduledTime: newScheduledTime,
  });

  const orderedObjects = PojoMap.values(byId);
  const orderedIds = sortBy(orderedObjects, 'scheduledTime').map((p) => p.id);

  return { byId, orderedIds };
};

/**
 * Creates a temporary copy of current scheduled posts with scheduled times
 * updated for a batch reschedule. Re-sorts the list by scheduledTime so that
 * it feeds through the data selectors properly.
 *
 * @param scheduledPosts - Normalized scheduled posts
 * @param updates - List of postIds to reschedule and new scheduled times
 */
export const getTemporaryScheduledPostsForBatchReschedule = (
  scheduledPosts: NormalizedScheduledPosts,
  updates: ScheduledPostTimeUpdate[],
): NormalizedScheduledPosts => {
  return updates.reduce((posts, update) => {
    const updatePostableId = update.postableId;
    const post = Normalized.getById(posts, updatePostableId);

    if (!post) {
      return posts;
    }

    const byId = PojoMap.set(posts.byId, updatePostableId, {
      ...post,
      scheduledTime: update.scheduledTime,
    });

    const orderedObjects = PojoMap.values(byId);

    const orderedIds = sortBy(orderedObjects, 'scheduledTime').map((p) => p.id);

    return { byId, orderedIds } as NormalizedScheduledPosts;
  }, scheduledPosts);
};

/**
 * A standardized helper to navigate to the post details "Create" flow for a given post type.
 */
export const navigateToCreatePostFlow = (
  postType: InstagramPostType,
  scheduleOptions: ScheduleOptions,
) =>
  navigateToState('instagramAccount.schedule.postDetails', {
    postType,
    ...scheduleOptions,
  });

/**
 * A standardized helper to navigate to post details for a given post.
 */
export const navigateToEditPostFlow = (
  postId: PostableId,
  isUniversalSchedulerEnabled: boolean,
) => {
  if (isUniversalSchedulerEnabled) {
    void navigateToState('schedule.scheduler', {
      scheduledPostId: postId,
      entryNetwork: SocialNetwork.Instagram,
    });
  } else {
    void navigateToState('instagramAccount.schedule.postDetails', {
      targetScheduledPostId: postId,
    });
  }
};

/**
 * The feed preview should always show all scheduled content with a
 * scheduled time of two weeks prior to today or later. This minimum
 * date is used to make sure that we fetch scheduled data at least as
 * far back as this when we refresh the posts, and that we don't show
 * any content scheduled prior to this in the preview.
 */
export const getFeedPreviewCutoffDate = (tz: TimeZoneId) =>
  DateString.addDays(DateString.getToday(tz), -14);

/**
 * Builds a moment date range from the given parameters or by looking at the current date info.
 *
 * @returns a tuple of [ startMoment, endMoment | undefined ]
 *   - startMoment: The earlier of the given startDate or current startDate and the feed preview cutoff date
 *   - endMoment: The given endDate, or undefined if none is given
 */
export const toStartAndEndMoment = (
  state: ICuralateStore,
  startDate?: DateString,
  endDate?: DateString,
): [Moment, Moment | undefined] => {
  const tz = getUserTimeZone(state);

  const start = (() => {
    // If we didn't pass in a start date, get the current one out of the store
    const startDateStr = startDate ?? getCurrentStartDate(state);

    // We want to fetch data for the earliest date between the feed preview
    // cutoff date and the current calendar view's startDate.
    return moment.min(
      DateString.toMoment(startDateStr, tz),
      DateString.toMoment(getFeedPreviewCutoffDate(tz), tz),
    );
  })();

  /**
   * DateString.toMoment gives us the appropriate moment for 12AM on the given date.
   * We need to set this to the endOfDay to ensure we get all of the posts for that day.
   */
  const end = endDate && DateString.toMoment(endDate, tz).endOf('day');

  return [start, end];
};

/**
 * Given an optional set of story group clips, returns an array of the text values of all customizations
 * within a story group for the purpose of searching for a given captionSearchText value.
 */
export const getStoryGroupCustomizationText = (
  clips?: IInstagramStoryGroupClip[],
) => {
  if (!clips) {
    return [];
  }

  return clips
    .map((clip) =>
      clip.customizations.map((customization) => customization.value),
    )
    .flat()
    .filter(isDefined);
};

type IScheduledFeedPreviewItem = IFeedPreviewItem &
  Required<Pick<IFeedPreviewItem, 'postId' | 'scheduledTime'>>;

/**
 * Type guard to ensure item is a Scheduled Feed Preview Item with a defined
 * postId and scheduledTime
 *
 * @param item - a feed preview item
 */
export const isScheduledFeedPreviewItem = (
  item: IFeedPreviewItem,
): item is IScheduledFeedPreviewItem => {
  return isDefined(item.postId) && isDefined(item.scheduledTime);
};

/**
 * Merges a list of normalized scheduled posts together and re-sorts the ordered ids by scheduled time ascending.
 */
export const mergeScheduledItemLists = (
  scheduledItemLists: NormalizedScheduledPosts[],
): NormalizedScheduledPosts => {
  const mergedById = mergeObjects(
    scheduledItemLists.map((scheduledItems) => scheduledItems.byId),
  );
  const mergedItems = PojoMap.values(mergedById);

  const mergedOrderedIds = sortBy(mergedItems, 'scheduledTime').map(
    (item) => item.id,
  );

  return {
    byId: mergedById,
    orderedIds: mergedOrderedIds,
  };
};

/**
 * Sorts scheduled feed posts, story groups, and reels posts by scheduled time ascending.
 */
export const sortScheduledPosts = (
  scheduledUniversalPosts: IScheduledInstagramPost[],
): IScheduledInstagramPost[] => {
  return sortBy(scheduledUniversalPosts, 'scheduledTime');
};

export const toIgPostStatus = (
  postStatus: PostableStatus,
): InstagramPostStatus => {
  switch (postStatus) {
    case PostableStatus.Cancelled:
      return InstagramPostStatus.Canceled;
    case PostableStatus.Dispatched:
      return InstagramPostStatus.Scheduled;
    case PostableStatus.Failed:
      return InstagramPostStatus.Failed;
    case PostableStatus.Pending:
      return InstagramPostStatus.Scheduled;
    case PostableStatus.Posted:
      return InstagramPostStatus.Posted;
    case PostableStatus.Processing:
      return InstagramPostStatus.Scheduled;
    case PostableStatus.ProcessingComplete:
      return InstagramPostStatus.Notified;
    case PostableStatus.Rescheduled:
      return InstagramPostStatus.FailedRescheduled;
    case PostableStatus.Scheduled:
      return InstagramPostStatus.Scheduled;
  }
};

export const isInstagramPost = (
  post: IScheduledPost | IDraftPost,
): post is IScheduledInstagramPost | IDraftInstagramPost => {
  switch (post.postType) {
    case UniversalPostType.InstagramFeedAutomaticPost:
    case UniversalPostType.InstagramFeedManualPost:
    case UniversalPostType.InstagramReelsAutomaticPost:
    case UniversalPostType.InstagramReelsManualPost:
    case UniversalPostType.InstagramStoryGroupPost:
    case UniversalPostType.InstagramStoryGroupAutomaticPost:
      return true;
    default:
      return false;
  }
};

export const isInstagramScheduledPost = (
  post: IScheduledPost,
): post is IScheduledInstagramPost => {
  switch (post.postType) {
    case UniversalPostType.InstagramFeedAutomaticPost:
    case UniversalPostType.InstagramFeedManualPost:
    case UniversalPostType.InstagramReelsAutomaticPost:
    case UniversalPostType.InstagramReelsManualPost:
    case UniversalPostType.InstagramStoryGroupPost:
    case UniversalPostType.InstagramStoryGroupAutomaticPost:
      return true;
    default:
      return false;
  }
};

export const isInstagramDraftPost = (
  post: IDraftPost,
): post is IDraftInstagramPost => {
  switch (post.postType) {
    case UniversalPostType.InstagramFeedAutomaticPost:
    case UniversalPostType.InstagramFeedManualPost:
    case UniversalPostType.InstagramReelsAutomaticPost:
    case UniversalPostType.InstagramReelsManualPost:
    case UniversalPostType.InstagramStoryGroupPost:
    case UniversalPostType.InstagramStoryGroupAutomaticPost:
      return true;
    default:
      return false;
  }
};

export const isInstagramInitiatedPost = (post: IScheduledPost | IDraftPost) => {
  return (
    isScheduledPost(post) &&
    isInstagramScheduledPost(post) &&
    isInitiatedPost(post)
  );
};

export interface IInstagramStandardPostDetails {
  caption?: string;
  carouselMedia?: IInstagramCarouselItem[];
  customizations?: InstagramReelsPostCustomizations;
  firstComment?: string;
  isManual: boolean;
  location?: InstagramLocation;
  notifyUserId?: UserId;
  postType: InstagramPostType;
  previewMediaItem?: IMlsMediaItem;
  shareToFeed?: boolean;
  shopTags?: IShopTag[];
  shouldCreateFacebookCrossPost?: boolean;
  shouldCreateTwitterCrossPost?: boolean;
  storyClips?: IInstagramStoryGroupClip[];
  userTags?: ITaggedUser[];
  videoMetadata?: InstagramVideoMetadata;
}

export const getInstagramStandardPostDetails = (
  post: IScheduledInstagramPost | IDraftInstagramPost,
): IInstagramStandardPostDetails => {
  const { config, mediaItems } = post;
  const primaryCuid = config.cuids[0];
  const previewMediaItem = primaryCuid
    ? PojoMap.get<Cuid, IMlsMediaItem>(mediaItems, primaryCuid)
    : PojoMap.values(mediaItems).pop();

  const baseItem = {
    postType: InstagramPostType.Post,
    previewMediaItem,
    isManual: true,
  };

  switch (post.config['@type']) {
    case UniversalScheduledPostConfigType.InstagramFeedManualPost:
      return {
        ...baseItem,
        caption: post.config.caption,
        notifyUserId: post.config.notifyUserId,
        carouselMedia: post.config.carouselMedia?.flatMap((media) => {
          const item = PojoMap.get<Cuid, IMlsMediaItem>(mediaItems, media.cuid);
          if (!isDefined(item)) {
            return [];
          }
          return {
            cuid: media.cuid,
            mediaItem: item,
          };
        }),
        shouldCreateFacebookCrossPost:
          post.config.shouldCreateFacebookCrossPost,
        shouldCreateTwitterCrossPost: post.config.shouldCreateTwitterCrossPost,
      };
    case UniversalScheduledPostConfigType.InstagramFeedAutomaticPost:
      return {
        ...baseItem,
        caption: post.config.caption,
        location: post.config.location,
        userTags: post.config.userTags?.map(fromInstagramTaggedUser) ?? [],
        videoMetadata: post.config.videoMetadata,
        notifyUserId: post.config.notifyUserId,
        carouselMedia: post.config.carouselMedia?.flatMap((media) => {
          const item = PojoMap.get<Cuid, IMlsMediaItem>(mediaItems, media.cuid);
          if (!isDefined(item)) {
            return [];
          }
          return {
            cuid: media.cuid,
            mediaItem: item,
          };
        }),
        firstComment: post.config.firstComment,
        shopTags: post.config.shopTags?.map(toShopTag),
        shouldCreateFacebookCrossPost:
          post.config.shouldCreateFacebookCrossPost,
        shouldCreateTwitterCrossPost: post.config.shouldCreateTwitterCrossPost,
        isManual: false,
      };
    case UniversalScheduledPostConfigType.InstagramReelsManualPost:
      return {
        ...baseItem,
        caption: post.config.customizations?.caption ?? '',
        customizations: post.config.customizations,
        postType: InstagramPostType.Reels,
        shareToFeed: post.config.shareToFeed,
        notifyUserId: post.config.notifyUserId,
      };
    case UniversalScheduledPostConfigType.InstagramReelsAutomaticPost:
      return {
        ...baseItem,
        caption: post.config.customizations?.caption,
        customizations: post.config.customizations,
        postType: InstagramPostType.Reels,
        location: post.config.location,
        shareToFeed: post.config.shareToFeed,
        notifyUserId: post.config.notifyUserId,
        firstComment: post.config.firstComment,
        shopTags: post.config.shopTags?.map(toShopTag),
        isManual: false,
        videoMetadata: {
          thumbOffset: post.config.thumbOffset,
          coverFileId: post.config.coverFileId,
        },
      };
    case UniversalScheduledPostConfigType.InstagramStoryGroupAutomaticPost:
      return {
        ...baseItem,
        postType: InstagramPostType.Story,
        notifyUserId: post.config.notifyUserId,
        storyClips: post.config.cuids.map((cuid) => {
          return {
            cuid,
            customizations: [],
            mediaItem: isDefined(cuid)
              ? PojoMap.get<Cuid, IMlsMediaItem>(mediaItems, cuid)
              : undefined,
          };
        }),
        isManual: false,
      };
    case UniversalScheduledPostConfigType.InstagramStoryGroup:
      return {
        ...baseItem,
        postType: InstagramPostType.Story,
        notifyUserId: post.config.notifyUserId,

        storyClips: post.config.storyClips.map((clip) => {
          return {
            cuid: clip.cuid,
            customizations: clip.customizations.map((customization) => {
              return {
                value: customization.value,
                customizationType:
                  InstagramStoryGroupClipCustomizationType[
                    customization.customizationType
                  ],
              };
            }),
            mediaItem: isDefined(clip.cuid)
              ? PojoMap.get<Cuid, IMlsMediaItem>(post.mediaItems, clip.cuid)
              : undefined,
          };
        }),
      };
  }
};

export const getInstagramPostType = (
  post: IDraftInstagramPost | IScheduledInstagramPost,
) => {
  if (isScheduledOrDraftStoryGroup(post)) {
    return InstagramPostType.Story;
  } else if (isScheduledOrDraftReelsPost(post)) {
    return InstagramPostType.Reels;
  } else {
    return InstagramPostType.Post;
  }
};

/**
 * Helper utils to determine post states for common
 * pieces of specialized UI (ex: gallery icons).
 */
export const isGalleryPost = (post: IScheduledInstagramPost) =>
  post.config.cuids.length > 1 && !isStoryPost(post);

export const isStoryPost = ({ postType }: IScheduledInstagramPost) =>
  postType === UniversalPostType.InstagramStoryGroupAutomaticPost ||
  postType === UniversalPostType.InstagramStoryGroupPost;

export const isReelsPost = ({ postType }: IScheduledInstagramPost) =>
  postType === UniversalPostType.InstagramReelsAutomaticPost ||
  postType === UniversalPostType.InstagramReelsManualPost;

export const isVideoPost = (
  post: IScheduledInstagramPost,
): post is IScheduledInstagramPost & {
  mediaItem: {
    networkItem: WithVideo<INetworkItem>;
  };
} => {
  const { previewMediaItem } = getInstagramStandardPostDetails(post);
  return (
    isDefined(previewMediaItem?.networkItem) &&
    isVideo(previewMediaItem.networkItem)
  );
};

/**
 * Gets the media type overlay icon type for scheduled items in the Feed Preview
 */
export const getMediaOverlayIconType = (
  post: IScheduledInstagramPost,
): FeedPreviewMediaOverlayIconType => {
  if (isGalleryPost(post)) {
    return 'gallery';
  } else if (isScheduledOrDraftReelsPost(post)) {
    return 'Reels';
  } else if (isVideoPost(post)) {
    return 'video';
  } else {
    return 'image';
  }
};

export const isAutoPost = (post: IScheduledInstagramPost) => {
  const postDetails = getInstagramStandardPostDetails(post);
  return !postDetails.isManual;
};

export const isFailedPost = ({ status }: IScheduledInstagramPost) =>
  status === PostableStatus.Failed;

/**
 * Returns whether the post should appear as "Posted" in the IG calendar.
 */
export const isPostedPost = (
  post: IScheduledInstagramPost | IDraftInstagramPost,
) => {
  if (isDraftPost(post)) {
    return false;
  }
  const postDetails = getInstagramStandardPostDetails(post);
  const { customStatus, postType, status } = post;
  // When a user finishes posting a manual post that has poor matching performance (i.e. story and reels posts),
  // we want to immediately show the "Posted" status instead of waiting for the post to be matched.
  // In the mobile app, we indicate these posts are "done" by setting the user action status to 'PostHidden'.
  const shouldUseUserActionStatusForPostedBadge =
    postType === UniversalPostType.InstagramStoryGroupAutomaticPost ||
    postType === UniversalPostType.InstagramStoryGroupPost ||
    postType === UniversalPostType.InstagramReelsAutomaticPost ||
    postType === UniversalPostType.InstagramReelsManualPost;

  return (
    (shouldUseUserActionStatusForPostedBadge &&
      customStatus === UniversalPostableCustomStatus.PostHidden) ||
    // When an automatic post has been published to IG successfully it is in status ProcessingComplete
    // until we match it. We want to immediately show the "Posted" status in this state rather than waiting
    // for the post to be matched.
    (!postDetails.isManual && status === PostableStatus.ProcessingComplete) ||
    status === PostableStatus.Posted
  );
};

export const isInitiatedPost = ({ customStatus }: IScheduledInstagramPost) => {
  return customStatus === UniversalPostableCustomStatus.PostInitiated;
};

export const isHiddenPost = ({ customStatus }: IScheduledInstagramPost) =>
  customStatus === UniversalPostableCustomStatus.PostHidden;

export function isScheduledOrDraftFeedPost(
  item: IScheduledInstagramPost | IDraftInstagramPost,
) {
  return (
    item.postType === UniversalPostType.InstagramFeedAutomaticPost ||
    item.postType === UniversalPostType.InstagramFeedManualPost
  );
}

export function isScheduledOrDraftStoryGroup(
  item: IScheduledInstagramPost | IDraftInstagramPost,
) {
  return (
    item.postType === UniversalPostType.InstagramStoryGroupAutomaticPost ||
    item.postType === UniversalPostType.InstagramStoryGroupPost
  );
}

export function isScheduledOrDraftReelsPost(
  item: IScheduledInstagramPost | IDraftInstagramPost,
) {
  return (
    item.postType === UniversalPostType.InstagramReelsAutomaticPost ||
    item.postType === UniversalPostType.InstagramReelsManualPost
  );
}
